// ═══════════════════════════════════════════════════════════════════
//  RESCHEDULE TARGETS
//
//  The manual reschedule workspace (AB#811, SU10) draws the admin
//  calendar's team-day grid in a selection mode: one booking is active,
//  and every cell says whether that booking may move there. This is the
//  "whether", for one host column and one row. Pure — no fetching, no
//  clock: "now" is read off the day read's own `earliestStart`.
//
//  It mirrors ManualReassignmentValidator's order, so a cell offered here
//  is one the server will accept — rule 1 included, from the day read's
//  `elsewhere` (the batch re-checks it against Cal at run time). The
//  server stays authoritative: a 400 from it still wins over anything
//  drawn here.
//
//  Every time is Pacific wall clock: the day read's "08:30:00" windows
//  and the grid's rows are both minutes past midnight on the site's
//  clock, and the instants it carries (busy, earliestStart) are read on
//  that clock before they are compared.
// ═══════════════════════════════════════════════════════════════════

import type { ManualResolveDay, ManualResolveDayHost } from "@/app/appointments/schedule-overrides/scheduleOverridesApi"
import type { SlotData } from "./dayGrid"
import { SITE_TIME_ZONE, timeStringToMinutes, zonedDateAndMinutes, zonedTimeToInstant } from "./siteTime"
import type { CalendarDate, MinuteOfDay } from "./siteTime"

/**
 * What a cell shows in selection mode, on top of what it is.
 *
 * - `valid`: the active booking may move here; clickable even where the grid would not offer a booking.
 * - `invalid`: it may not; not clickable, and `reason` says why on hover.
 * - `pending`: a pairing not yet applied lands here; `label` names its booking, and clicking undoes it.
 * - `active-origin`: where the active booking is now.
 */
export interface SlotDecoration {
  kind: "valid" | "invalid" | "pending" | "active-origin"
  /** The booking's name: the active one on a `valid` cell, the paired one on a `pending` cell. */
  label?: string
  /** Why, on an `invalid` cell. */
  reason?: string
  /** The pairing's booking request, on a `pending` cell: which pairing a click there undoes. */
  requestId?: string
}

/** The booking being placed: one of the override's affected bookings. */
export interface ActiveBooking {
  requestId: string
  /** Its site, which a move keeps (decision ams-2db). */
  teamSlug: string
  /** Its slot type (event type slug), which a move keeps. */
  eventTypeSlug: string
  /** How long it runs; a target must fit the whole of it. */
  durationMinutes: number
  /** ISO instant: where it is now. */
  currentStart: string
  /** Its host now, to mark its current slot; null when unknown. */
  currentCalUserId: number | null
  /** The customer's name, for the cells' labels. */
  label?: string
}

/** One pairing not yet applied: booking request id → the host and start it moves to. */
export interface PendingPairing {
  calUserId: number
  /** ISO instant: the target start, as it will be sent. */
  start: string
  /** The paired booking's length. */
  durationMinutes: number
  /** The paired booking's name, for the pending cell. */
  label?: string
}

/** The workspace's pairings, by booking request id. A booking is paired at most once. */
export type PendingPairings = Readonly<Record<string, PendingPairing>>

/** A pairing placed on its host's Pacific day. */
export interface PlacedPairing {
  requestId: string
  startMinutes: MinuteOfDay
  endMinutes: MinuteOfDay
  label?: string
}

/** The key `pairingsByHostDay` files a pairing under. */
export function hostDayKey(calUserId: number, date: CalendarDate): string {
  return `${calUserId}|${date}`
}

/**
 * The pairings grouped by target host and Pacific date (`hostDayKey`), each as minutes on that day —
 * what a cell checks for overlap and what counts against the host's cap that day. Computed once per
 * change of pairings rather than once per cell.
 */
export function pairingsByHostDay(
  pairings: PendingPairings,
  timeZone: string = SITE_TIME_ZONE,
): ReadonlyMap<string, readonly PlacedPairing[]> {
  const grouped = new Map<string, PlacedPairing[]>()
  for (const [requestId, pairing] of Object.entries(pairings)) {
    const { date, minutes } = zonedDateAndMinutes(pairing.start, timeZone)
    const key = hostDayKey(pairing.calUserId, date)
    const list = grouped.get(key) ?? []
    list.push({ requestId, startMinutes: minutes, endMinutes: minutes + Math.max(pairing.durationMinutes, 1), label: pairing.label })
    grouped.set(key, list)
  }
  return grouped
}

// ── Reasons ──────────────────────────────────────────────────────────
// Short forms of ManualReassignmentValidator's, for a tooltip.

export const NO_DAY_REASON = "The rules for this day are still loading."
export const DAY_FAILED_REASON = "The rules for this day could not be read, so this slot cannot be offered. Try refreshing."
export const OTHER_SITE_REASON = "Not this appointment's site: a move keeps the site."
export const PAST_REASON = "This time has passed."
export const noticeReason = (minutes: number) =>
  `Inside the scheduling system's minimum booking notice of ${formatHours(minutes)}.`
export const horizonReason = (lastDate: CalendarDate) =>
  `After ${lastDate}, the last day a move may reach (the 28-day booking window plus the reassignment horizon).`
export const BOOKING_WINDOW_REASON =
  "Past the 28-day booking window: this needs the Cal update (the booking-window bypass is off)."
export const notAvailableReason = (eventTypeSlug: string) =>
  `The host is not available for ${eventTypeSlug} for the whole appointment at this time (a move keeps the slot type).`
export const NOT_A_HOST_REASON = "This person does not host at this site on this day."
export const BOOKED_REASON = "The host already has an appointment, or a pending change, overlapping this time."
export const cappedReason = (limit: number) =>
  `The host would go over their daily limit of ${limit} appointment${limit === 1 ? "" : "s"}, counting pending changes.`
export const elsewhereReason = (teamSlug: string) =>
  `The host is available at ${teamSlug} at this time (one site per host per slot).`

function formatHours(minutes: number): string {
  if (minutes % 60 === 0) {
    const hours = minutes / 60
    return `${hours} hour${hours === 1 ? "" : "s"}`
  }
  return `${minutes} minutes`
}

// ── Validity ─────────────────────────────────────────────────────────

export interface TargetValidityInput {
  /** The booking being placed; null when none is active, which marks only the pending cells. */
  active: ActiveBooking | null
  /** The day read for the site and date in view; null while it loads or when it failed. */
  day: ManualResolveDay | null | undefined
  /** The grid's own date ("yyyy-MM-dd", Pacific): where pending pairings are placed while there is no day read. */
  date?: CalendarDate
  /** Whether the day read failed: the cells then say so rather than "still loading". */
  dayFailed?: boolean
  /** The column's host. */
  calUserId: number
  /** The row: minutes past midnight, Pacific. */
  startMinutes: MinuteOfDay
  /** The grid's row length — the team's slot minutes. */
  slotMinutes: number
  /** `pairingsByHostDay` of the workspace's pairings. */
  pairings: ReadonlyMap<string, readonly PlacedPairing[]>
  /** The zone the day read's instants are read in. Pacific. */
  timeZone?: string
}

/**
 * What one cell (a host column, a row) shows for the active booking — undefined for no decoration.
 *
 * In order: a pending pairing on the row; with no booking active, nothing more; the active booking's
 * own slot; then the server's rules, first broken wins — the booking's own site; not passed, and clear
 * of the minimum notice; no later than `lastDate`; past `windowLastDate` only with the bypass; the host
 * effectively available for the booking's own slot type across the whole appointment; overlapping
 * none of the host's appointments (those moving away in this batch included: they still count where
 * they are) nor a pending pairing on the host; and under the host's daily cap counting the pending
 * pairings onto that day; then rule 1, no available window at another of the host's sites
 * (`elsewhere`) overlapping the appointment. The active booking's own appointment and pairing are left
 * out, as the server leaves them out: a move may stay with the same host and day.
 */
export function targetValidity(input: TargetValidityInput): SlotDecoration | undefined {
  const { active, day, calUserId, startMinutes, pairings } = input
  const timeZone = input.timeZone ?? SITE_TIME_ZONE
  const slotMinutes = input.slotMinutes > 0 ? input.slotMinutes : 30

  // A pairing lands on this row: drawn whichever booking is active, since it is the way to undo it. Placed
  // by the grid's own date while the day read loads or has failed, so a pending pairing never vanishes.
  const placedDate = day?.date ?? input.date
  if (placedDate) {
    const placed = pairings.get(hostDayKey(calUserId, placedDate)) ?? []
    const here = placed.find(p => overlaps(startMinutes, startMinutes + slotMinutes, p.startMinutes, p.endMinutes))
    if (here) {
      return { kind: "pending", label: here.label, requestId: here.requestId }
    }
  }

  if (!active) return undefined
  if (!day) return { kind: "invalid", reason: input.dayFailed ? DAY_FAILED_REASON : NO_DAY_REASON }

  // Where it is now. Marked rather than judged: moving a booking onto its own slot moves nothing.
  if (active.currentCalUserId === calUserId) {
    const current = zonedDateAndMinutes(active.currentStart, timeZone)
    if (current.date === day.date
      && overlaps(startMinutes, startMinutes + slotMinutes, current.minutes, current.minutes + Math.max(active.durationMinutes, 1))) {
      return { kind: "active-origin", label: active.label }
    }
  }

  const invalid = (reason: string): SlotDecoration => ({ kind: "invalid", reason })

  // The page only draws the booking's own site, but a day read for another one must not offer it.
  if (day.teamSlug !== active.teamSlug) return invalid(OTHER_SITE_REASON)

  // Passed, then inside the notice: "now" is the read's earliest start less the notice it added.
  const target = zonedTimeToInstant(day.date, startMinutes, timeZone).getTime()
  const earliest = Date.parse(day.earliestStart)
  const now = earliest - (day.minimumNoticeMinutes * 60_000)
  if (target <= now) return invalid(PAST_REASON)
  if (target < earliest) return invalid(noticeReason(day.minimumNoticeMinutes))

  // Calendar dates compare as strings: "yyyy-MM-dd" sorts as it reads.
  if (day.date > day.lastDate) return invalid(horizonReason(day.lastDate))
  if (day.date > day.windowLastDate && !day.bypassEnabled) return invalid(BOOKING_WINDOW_REASON)

  const host = day.hosts.find(h => h.calUserId === calUserId)
  if (!host) return invalid(NOT_A_HOST_REASON)

  const duration = Math.max(active.durationMinutes, 1)
  const endMinutes = startMinutes + duration
  if (endMinutes >= 24 * 60 || !coveredByWindow(host, active.eventTypeSlug, startMinutes, endMinutes)) {
    return invalid(notAvailableReason(active.eventTypeSlug))
  }

  // Booked: every appointment the host has that day bar the one being placed — the ones paired to move
  // away included, because the batch may not have moved them first — and every other pending pairing.
  const others = host.busy.filter(b => b.requestId !== active.requestId)
  const clash = others.some(b => {
    const start = zonedDateAndMinutes(b.start, timeZone)
    if (start.date !== day.date) return false
    const length = b.end ? Math.round((Date.parse(b.end) - Date.parse(b.start)) / 60_000) : 0
    return overlaps(startMinutes, endMinutes, start.minutes, start.minutes + (length > 0 ? length : 1))
  })
  const placedOnHost = (pairings.get(hostDayKey(calUserId, day.date)) ?? []).filter(p => p.requestId !== active.requestId)
  if (clash || placedOnHost.some(p => overlaps(startMinutes, endMinutes, p.startMinutes, p.endMinutes))) {
    return invalid(BOOKED_REASON)
  }

  // The cap, counting the pairings onto the day; the booking being placed does not count twice when it
  // is already one of the host's that day.
  if (host.limit != null) {
    const ownHere = host.busy.length - others.length
    const load = host.booked - ownHere + placedOnHost.length
    if (load >= host.limit) return invalid(cappedReason(host.limit))
  }

  // Rule 1: one site per host. The first other site (by slug, as the server orders them) with a window
  // overlapping the appointment.
  const elsewhere = Object.keys(host.elsewhere ?? {}).sort().find(site =>
    (host.elsewhere?.[site] ?? []).some(w => overlaps(startMinutes, endMinutes, timeStringToMinutes(w.start), timeStringToMinutes(w.end))))
  if (elsewhere) return invalid(elsewhereReason(elsewhere))

  return { kind: "valid", label: active.label }
}

/**
 * A cell the grid draws as booked in Cal is never offered, even where the day read (CMS's own Booked
 * appointments) misses it — a booking CMS has no row for, drift, or a status other than Booked. A `valid`
 * decoration on such a cell becomes `invalid` with the booked reason, unless the booking there is the
 * active one itself (its own slot: moving it within its own time is allowed, as the server allows it).
 */
export function guardBookedCell(decoration: SlotDecoration | undefined, cell: Pick<SlotData, "state" | "booking">, active: ActiveBooking | null): SlotDecoration | undefined {
  if (decoration?.kind !== "valid" || cell.state !== "booked") return decoration
  const there = cell.booking?.requestId?.toLowerCase()
  if (active && there && there === active.requestId.toLowerCase()) return decoration
  return { kind: "invalid", reason: BOOKED_REASON }
}

/** Whether `[start, end)` lies wholly inside one of the host's windows for the slot type. */
function coveredByWindow(host: ManualResolveDayHost, eventTypeSlug: string, start: MinuteOfDay, end: MinuteOfDay): boolean {
  const windows = host.available[eventTypeSlug] ?? []
  return windows.some(w => timeStringToMinutes(w.start) <= start && end <= timeStringToMinutes(w.end))
}

function overlaps(aStart: number, aEnd: number, bStart: number, bEnd: number): boolean {
  return aStart < bEnd && bStart < aEnd
}

/**
 * The Cal team id the grid needs for an override's site slug, or null until the teams have loaded and
 * one matches. `useTeamDay` falls back to the first team for an id it does not know, so a page must not
 * mount the grid until this is a number.
 */
export function teamIdForSlug(teams: readonly { id: number; slug?: string | null }[], teamSlug: string | null | undefined): number | null {
  if (!teamSlug) return null
  return teams.find(t => t.slug === teamSlug)?.id ?? null
}
